// timeline — peaks waveform + bar grid + sections overlay + playhead.
//
// Class names match the real desktop rehearsal overview band so the imported
// @desktop-css/rehearsal/waves-column.css (scoped under `.rh-waves
// .rh-overview-band …`) styles this widget without duplication. See
// app/renderer/ui/player-panel.js (lines ~171-194) for the desktop template
// this mirrors and app/renderer/ui/rehearsal/waves-column.css for the rules.
//
// Layered structure:
//   .rh-waves                           (outer; required ancestor for desktop CSS)
//     .rh-overview-band                 (positioned container, grid-row:1)
//       .rh-overview-scroll             (scroll layer)
//         .rh-overview-content          (position:relative content)
//           <svg class="tl-svg">        Layer 1: peaks ribbon (drawSymRibbon)
//           <canvas class="rh-bar-grid">Layer 2: bar lines + numbers (drawBarGrid)
//           <div class="rh-sections">   Layer 3: section ribbons (renderSectionsBySec)
//   .rh-playhead                        Layer 4: vertical playhead (mountPlayheadOverlay,
//                                       appended at .rh-waves level so it spans full height)
//
// All inner layers share a single `.rh-overview-content` host that's
// `position:relative`. Peaks/grid use a fixed 1000×80 viewBox/canvas; CSS
// scales them to the host's actual width via SVG `preserveAspectRatio="none"`
// and the canvas's stretched style. Sections and playhead use
// percentage-based positioning, so they always track the rendered width
// regardless of canvas/SVG scaling.
//
// API change vs C.1/C.2: takes `manifest` and `peaks` as constructor args
// (no state-store subscription). The orchestrator (main.ts) loads them once
// and hands them in. The playhead overlay polls `player.getPlayhead()` via
// RAF — no need to wire state.playhead through the timeline.

import type { ShareManifest, SummaryPeaks } from '@bandway/share-types';
import {
  drawSymRibbon,
  drawBarGrid,
  renderSectionsBySec,
  mountPlayheadOverlay,
  matchSemantic,
} from '@bandway/ui-primitives';
import type { Player } from '../player.js';

export interface TimelineHandle {
  unmount: () => void;
}

const W = 1000;
const H = 80;

export function mountTimeline(
  host: Element,
  manifest: ShareManifest,
  peaks: SummaryPeaks,
  player: Player,
): TimelineHandle {
  host.textContent = '';

  // Outer .rh-waves — required ancestor for desktop CSS selectors like
  // `.rh-waves .rh-overview-band`, `.rh-waves .rh-bar-grid`, etc.
  const root = document.createElement('div');
  root.className = 'rh-waves';

  // .rh-overview-band — the actual overview container; receives padding,
  // hover state for bar-grid opacity, zoom mode etc. from desktop CSS.
  const band = document.createElement('div');
  band.className = 'rh-overview-band';
  root.appendChild(band);

  // .rh-overview-scroll → .rh-overview-content — inner wrappers mirror the
  // desktop template so positioned children (.tl-svg / .rh-bar-grid /
  // .rh-sections) inherit the same containing block as on desktop.
  const scroll = document.createElement('div');
  scroll.className = 'rh-overview-scroll';
  band.appendChild(scroll);

  const content = document.createElement('div');
  content.className = 'rh-overview-content';
  scroll.appendChild(content);

  // Layer 1: peaks SVG. drawSymRibbon returns an `<svg>…</svg>` string;
  // parse it via a temp container so we can stamp the real desktop class
  // (`tl-svg`) onto the SVG element itself, matching desktop markup.
  const tmp = document.createElement('div');
  tmp.innerHTML = drawSymRibbon(peaks.peaks, {
    width: W,
    height: H,
    fillStyle: 'currentColor',
    barWidth: 2,
    barGap: 1,
  });
  const svgEl = tmp.querySelector('svg');
  if (svgEl) {
    svgEl.setAttribute('class', 'tl-svg');
    content.appendChild(svgEl);
  }

  // Layer 2: bar grid. Desktop uses a `<div class="rh-bar-grid">` with bar
  // ticks as child divs (rendered by waves-column.js); web reuses the same
  // class on a `<canvas>` since drawBarGrid paints into a 2D context.
  // The `.rh-waves .rh-bar-grid { position:absolute; inset:32px 8px 0 8px }`
  // rule applies equally to a canvas element.
  const gridCanvas = document.createElement('canvas');
  gridCanvas.className = 'rh-bar-grid';
  gridCanvas.width = W;
  gridCanvas.height = H;
  const ctx = gridCanvas.getContext('2d');
  if (ctx && manifest.duration > 0) {
    drawBarGrid(ctx, {
      bpm: manifest.song.bpm,
      timeSignature: manifest.song.timeSignature,
      durationSec: manifest.duration,
      pxPerSec: W / manifest.duration,
      height: H,
      barOffsetMs: (manifest.song as { barOffsetMs?: number }).barOffsetMs ?? 0,
    });
  }
  content.appendChild(gridCanvas);

  // Layer 3: sections overlay — ribbons positioned by seconds.
  const sectionsLayer = document.createElement('div');
  sectionsLayer.className = 'rh-sections';
  const sectionEls = renderSectionsBySec(
    manifest.sections.map((s) => ({
      id: s.id,
      name: s.name,
      startSec: s.startSec,
      endSec: s.endSec,
      color: matchSemantic(s.name) || 'var(--fg-3)',
    })),
    manifest.duration,
    0,
  );
  sectionEls.forEach((el) => sectionsLayer.appendChild(el));
  content.appendChild(sectionsLayer);

  host.appendChild(root);

  // Layer 4: playhead overlay (managed by primitive — RAF + click-seek).
  // Primitive emits `.rh-playhead` and appends to the passed host; we mount
  // on the outer `.rh-waves` so the playhead spans the band visually (same
  // grid-row:1/span 4 trick as desktop scales to a single-row band here).
  const overlay = mountPlayheadOverlay(root as HTMLElement, {
    getPositionSec: () => player.getPlayhead(),
    getTotalDurationSec: () => manifest.duration,
    onClickSeek: (sec: number) => player.seek(sec),
  });
  overlay.start();

  return {
    unmount: () => {
      overlay.dispose();
      host.textContent = '';
    },
  };
}
